import React, { FC, useMemo, useEffect } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import { updateNewOrderCommodities } from '../../../redux/features/orders/ordersSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { TwAccordion, TwAccordionDetails, TwAccordionSummary } from '../../TwAccordion/TwAccordion';

import { SxProps, Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { MeasurementSystem } from '../../../providers/User/User.model';
import { CommodityList } from '../../../redux/api/commodities/commodities.model';
import './TwCommoditiesBuilder.scss';
import { useCommoditySettings } from '../../../hooks/useCommoditySettings';
import TwCommodityFooter from './desktop/TwCommodityFooter';
import { TwCommodityRow } from './desktop/TwCommodityRow';
import TwTripCommodityFromOrderRow from './desktop/TwTripCommodityFromOrderRow';
import TwMobileCommodityRow from './mobile/TwMobileCommodityRow';
import TwMobileCommodityFooter from './mobile/TwMobileCommodityFooter';
import TwMobileTripCommodityFromOrderRow from './mobile/TwMobileTripCommodityFromOrderRow';
import { EntityType } from '../../../redux/Global.model';
import { generateNewTripCommodity } from '../../../utils/commodity/commodityUtils';
import { generateNewOrderCommodity } from '../../../utils/commodity/commodityUtils';
import { updateNewTripCommodity } from '../../../redux/features/trip/tripSlice';

interface TwCommoditiesBuilderProps {
  isImport: boolean;
  sxAccordion?: SxProps;
  title?: string;
  overlayView?: boolean;
  entity: EntityType;
}

const TwCommoditiesBuilder: FC<TwCommoditiesBuilderProps> = ({
  isImport,
  sxAccordion,
  title,
  entity,
}): JSX.Element => {
  const { commoditySettings } = useCommoditySettings();
  const measurementSystem = commoditySettings?.measurement_system ?? MeasurementSystem.IMPERIAL;

  const { new_order_commodities = [] } = useAppSelector(
    (state) => state.orders
  );
  const { new_trip_commodities = [] } = useAppSelector(
    (state) => state.trip
  );
  const commoditiesToUse: CommodityList[] =
    entity === EntityType.ORDER ? new_order_commodities : new_trip_commodities;

  const dispatch = useAppDispatch();

  const { addNotification } = useNotifications();
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  const addNewCommodity = (): void => {
    if (commoditiesToUse.length === 20) {
      addNotification({
        message: 'You can only add up to 20 commodities',
        variant: 'error'
      });
    } else {
      if (entity === EntityType.ORDER) {
        dispatch(
          updateNewOrderCommodities([
            ...commoditiesToUse,
            generateNewOrderCommodity(measurementSystem)
          ])
        );
      } else {
        dispatch(
          updateNewTripCommodity([
            ...commoditiesToUse,
            generateNewTripCommodity(measurementSystem)
          ])
        );
      }
    }
  };

  const totalWeight = useMemo(() => {
    return commoditiesToUse.reduce((total, item: CommodityList) => {
      return total + (item.weight ? Number(item.weight) : 0);
    }, 0);
  }, [commoditiesToUse]);

  const totalQty = useMemo(() => {
    return commoditiesToUse.reduce((total, item) => {
      return total + (item.quantity ? Number(item.quantity) : 0);
    }, 0);
  }, [commoditiesToUse]);

  useEffect(() => {
    if (commoditiesToUse.length === 0) {
      addNewCommodity();
    }
  }, []);

  return (
    <TwAccordion defaultExpanded={true} sx={{ backgroundColor: '#F3F4F6', ...sxAccordion }}>
      <TwAccordionSummary expandIcon={<ExpandMoreIcon onClick={(): void => undefined} />}>
        <Typography
          sx={{
            fontSize: '12px',
            weight: '500',
            color: '#6B7280',
            marginLeft: '10px'
          }}
        >
          {title ? title : 'Commodities'}
        </Typography>
      </TwAccordionSummary>
      <TwAccordionDetails sx={{ padding: 2 }}>
        {/* Commodities */}
        {commoditiesToUse.map((item: CommodityList) => {
          // Mobile
          if (matchesMobileOrTablet || isImport) {
            if (entity === EntityType.TRIP && item.order_id) {
              return (
                <TwMobileTripCommodityFromOrderRow
                  key={item.ui_uid}
                  id={item.ui_uid}
                  commodity={item}
                  measurementSystem={measurementSystem}
                />
              );
            }
            return (
              <TwMobileCommodityRow
                key={item.ui_uid}
                id={item.ui_uid}
                commodity={item}
                measurementSystem={measurementSystem}
                entity={entity}
              />
            );
          }

          // Desktop
          if (entity === EntityType.TRIP && item.order_id) {
            return (
              <TwTripCommodityFromOrderRow
                key={item.ui_uid}
                commodity={item}
                measurementSystem={measurementSystem}
              />
            );
          }

          return (
            <TwCommodityRow
              key={item.ui_uid}
              ui_uid={item.ui_uid}
              commodity={item}
              measurementSystem={measurementSystem}
              entity={entity}
            />
          );
        })}

        {/* Footer */}
        {matchesMobileOrTablet || isImport ? (
          // Mobile and tablet footer
          <TwMobileCommodityFooter
            addNewCommodity={addNewCommodity}
            totalQty={totalQty}
            totalWeight={totalWeight}
            measurementSystem={measurementSystem}
          />
        ) : (
          // Desktop footer
          <TwCommodityFooter
            addNewCommodity={addNewCommodity}
            totalQty={totalQty}
            totalWeight={totalWeight}
            measurementSystem={measurementSystem}
          />
        )}
      </TwAccordionDetails>
    </TwAccordion>
  );
};
export default TwCommoditiesBuilder;
